import { NextPage } from 'next';
import Head from 'next/head';

import CardPage from 'components/pages/Card';
import GeneralLayout from 'layouts/GeneralLayout';
import fetchClient from 'utils/fetchClient.ts';
import { API_URLS } from 'constants/index';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const DOMAIN = 'https://bibikalka.com.ua';

interface ProductPageProps {
  related: Product.Item[];
  product: Product.Item;
  breadcrumbJsonLd: string;
  productJsonLd: string;
}

const Card: NextPage<ProductPageProps> = ({
  related, product, breadcrumbJsonLd, productJsonLd,
}) => {
  const ogImage = product?.assets?.items?.[0]?.src;
  // Products without a translated SEO block fall back to the translated product name
  const seoTranslation = { ...product.seo?.translation };
  Object.entries(product.valueTranslation ?? {}).forEach(([lang, value]) => {
    if (!seoTranslation[lang]?.title && !seoTranslation[lang]?.seoTitle && value?.name) {
      seoTranslation[lang] = { ...seoTranslation[lang], title: value.name };
    }
  });
  return (
    <GeneralLayout {...product.seo} translation={seoTranslation} ogImage={ogImage}>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: breadcrumbJsonLd }}
        />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: productJsonLd }}
        />
      </Head>
      <CardPage related={related} product={product} />
    </GeneralLayout>
  );
};

/** Strips HTML tags for use inside JSON-LD text fields */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s{2,}/g, ' ').trim();
}

function buildBreadcrumbJsonLd(product: Product.Item, locale: string): string {
  const localePrefix = locale === 'ru' ? '/ru' : '';
  const category = product.category as unknown as {
    name: string; link: string; translation?: { language: string; name: string }[];
  };
  const categoryName = (Array.isArray(category?.translation)
    && category.translation.find((tr) => tr.language === locale)?.name) || category?.name;
  const productName = product.valueTranslation?.[locale === 'ru' ? 'ru' : 'ua']?.name || product.name;
  const homeName = locale === 'ru' ? 'Главная' : 'Головна';

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: homeName,
        item: `${DOMAIN}${localePrefix}/`,
      },
      ...(category?.link ? [{
        '@type': 'ListItem',
        position: 2,
        name: categoryName || category.link,
        item: `${DOMAIN}${localePrefix}/category/${category.link}`,
      }] : []),
      {
        '@type': 'ListItem',
        position: category?.link ? 3 : 2,
        name: productName,
        item: `${DOMAIN}${localePrefix}/product/${product.link}`,
      },
    ],
  });
}

function buildProductJsonLd(product: Product.Item, locale: string): string {
  const localePrefix = locale === 'ru' ? '/ru' : '';
  const lang = locale === 'ru' ? 'ru' : 'ua';

  const localizedName = product.valueTranslation?.[lang]?.name || product.name;
  const rawDescription = product.promDescription?.[lang]
    || product.valueTranslation?.[lang]?.description
    || product.valueDescription
    || product.description
    || '';
  const description = stripHtml(rawDescription);

  const images = (product.assets?.items ?? [])
    .filter((a) => a.type === 'img')
    .map((a) => a.src);

  const price = String(product.discountPrice || product.price?.value || '0');
  const currency = product.price?.currency || 'UAH';
  const inStock = !product.outOfStock;

  const reviews = product.reviews ?? [];
  const hasRating = reviews.length > 0;
  const ratingValue = hasRating
    ? (reviews.reduce((s, r) => s + r.stars, 0) / reviews.length).toFixed(1)
    : null;

  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: localizedName,
    ...(description && { description }),
    ...(images.length > 0 && { image: images }),
    sku: product.display || product.link,
    ...(product.display && { mpn: product.display }),
    url: `${DOMAIN}${localePrefix}/product/${product.link}`,
    brand: {
      '@type': 'Brand',
      name: 'Бібікалка',
    },
    offers: {
      '@type': 'Offer',
      url: `${DOMAIN}${localePrefix}/product/${product.link}`,
      priceCurrency: currency,
      price,
      availability: inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: 'Бібікалка',
      },
    },
    ...(hasRating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue,
        reviewCount: reviews.length,
        bestRating: 5,
        worstRating: 1,
      },
      review: reviews.map((r) => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.name },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: r.stars,
          bestRating: 5,
          worstRating: 1,
        },
        reviewBody: r.description,
        datePublished: r.date ? r.date.split('T')[0] : undefined,
      })),
    }),
  });
}

export async function getServerSideProps(props: any) {
  const { params: { link }, locale, query: { currency } } = props;
  try {
    const response = await fetchClient(
      API_URLS.PRODUCT.LINK.replace('[:link]', link.toString()),
      { params: { currency: currency || 'UAH', lang: locale } },
    );

    if (response.status === 404 || response.status === 400) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
      };
    }

    const data = await response.json();
    const sanitize = (v: unknown) => JSON.parse(JSON.stringify(v, (_, val) => val ?? null));
    const product: Product.Item = sanitize(data.product);

    return {
      props: {
        related: sanitize(data.related),
        product,
        breadcrumbJsonLd: buildBreadcrumbJsonLd(product, locale as string),
        productJsonLd: buildProductJsonLd(product, locale as string),
        ...(await serverSideTranslations(locale, [
          'card', 'header', 'footer', 'layout',
        ])),
      },
    };
  } catch (e) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }
}

export default Card;
